import { NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { faFolderOpen } from '@fortawesome/free-solid-svg-icons';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { FormMode } from 'src/app/controls/common/form-mode';
import { DefaultManagementComponent } from 'src/app/controls/forms/default-management.component';
import { HttpError } from 'src/app/core/http-error';
import { Rights } from 'src/app/models/enums/rights';
import { FolderState } from 'src/app/models/folder-state';
import { IFolderState } from 'src/app/models/interfaces/folder-state.interface';
import { FoldersStatesService } from 'src/app/services/folders-states.service';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { DetailHeaderComponent } from '../../controls/detail-header/detail-header.component';
import { ListHeaderComponent } from '../../controls/list-header/list-header.component';
import { FolderStateDetailContext } from './folder-state-detail-context';
import { FolderStateDetailComponent } from './folder-state-detail/folder-state-detail.component';
import { FolderStatesListComponent } from './folder-states-list/folder-states-list.component';

enum DisplayMode {
    List,
    Form,
}

@Component({
    selector: 'app-folder-states',
    templateUrl: './folder-states.component.html',
    styleUrls: ['./folder-states.component.scss'],
    imports: [MatExpansionModule, NgClass, ListHeaderComponent, FolderStatesListComponent, DetailHeaderComponent, FolderStateDetailComponent],
})
export class FolderStatesComponent extends DefaultManagementComponent<IFolderState, FolderState, number> implements OnInit {
    /**
     * Liste des icônes utilisés dans le template
     */
    public faFolderState = faFolderOpen;

    /**
     * Liste des enums utilisés dans le template
     */
    public override FormMode = FormMode;
    public override DisplayMode = DisplayMode;

    /**
     * Contexte par défaut du formulaire contenant les données invariantes entre Workflow.
     */
    public defaultContext: FolderStateDetailContext = new FolderStateDetailContext();

    /**
     * Indique si le bouton d'ajout/Edit doit apparaitre.
     */
    public displayAddbuttom = true;
    public displayEditbuttom = true;

    /**
     *
     * @param notificationService : Service utilisé pour gérer l'affichage des notifications.
     * @param foldersStatesService : Service métier utilisé pour gérer les états de dossier.
     */
    constructor(
        protected override notificationService: NotificationService,
        private foldersStatesService: FoldersStatesService,
        private memoryService: MemoryService,
        private spinner: NgxSpinnerService
    ) {
        super(notificationService);
    }

    /**
     * Appellé lors de l'initialisation du composant.
     */
    ngOnInit() {
        this.displayAddbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.FolderStateCreate);
        this.displayEditbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.FolderStateEdit);
    }

    /**
     * Appellé lorsque l'utilisateur selectionne un folderState.
     * @param folderState Etat de dossier selectionné.
     */
    public onFolderStateSelected(folderState: FolderState) {
        this.spinner.show('main-spinner');
        this.createContext();

        this.loadFolderState(folderState.id).subscribe(
            (x) => {
                this.refresh(FolderState.createFormCopy(x), FormMode.Reading);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher l'état de dossier." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'icône 'Retour à la liste'.
     */
    public onBackToList() {
        this.setDisplayMode(DisplayMode.List);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Consulter' dans le menu contextuel.
     * @param folderState Etat de dossier sélectionné.
     */
    public onFolderStateDisplayed(folderState: FolderState) {
        this.spinner.show('main-spinner');
        this.createContext();

        this.loadFolderState(folderState.id).subscribe(
            (x) => {
                this.refresh(FolderState.createFormCopy(x), FormMode.Reading);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher l'état de dossier." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Editer' dans le menu contextuel.
     * @param folderState Etat de dossier sélectionné.
     */
    public onFolderStateEdited(folderState: FolderState) {
        this.spinner.show('main-spinner');
        this.createContext();

        // Cas de la mise à jour.
        this.loadFolderState(folderState.id).subscribe(
            (x) => {
                this.refresh(FolderState.createFormCopy(x), FormMode.Edition);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'éditer l'état de dossier." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Créer'.
     */
    public onFolderStateCreated() {
        this.displayForm(FolderState.createFormCopy(new FolderState()), FormMode.Creation);
    }

    /**
     * Appellé lorsque le contenu du formulaire a été sauvegardé.
     * @param folderState : Valeur de l'état de dossier retourné par le formulaire.
     */
    public onFolderStateUpdated(folderState: FolderState): void {
        this.spinner.show('main-spinner');
        if (this.context.editedItemId) {
            // Cas de la mise à jour.
            this.foldersStatesService.update(this.context.editedItemId, folderState).subscribe(
                (x) => {
                    this.refreshWithNotification(FolderState.createFormCopy(x), 'État de dossier mis à jour', FormMode.Reading);
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', "Impossible de mettre à jour l'état de dossier." + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        } else {
            // Cas de la création.
            this.foldersStatesService.add(folderState).subscribe(
                (x) => {
                    this.refreshWithNotification(FolderState.createFormCopy(x), 'État de dossier créé', FormMode.Reading);
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', "Impossible de créer l'état de dossier." + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        }
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Supprimer' dans le menu contextuel.
     * @param folderState Etat de dossier sélectionné.
     */
    public onFolderStateDeleted(folderState: FolderState) {
        this.spinner.show('main-spinner');
        // Suppression effective du folderState.
        this.foldersStatesService.delete(folderState.id).subscribe(
            (x) => {
                this.notificationService.displaySuccess(null, 'État de dossier supprimé');
                this.itemsList.refresh();
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible de supprimer l'état de dossier." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lors de l'export
     */
    public onFolderStatesExported() {
        this.createContext();
        this.itemsList.export('EtatsDeDossier.xlsx');
    }

    /**
     * Appellé lors de l'import
     */
    public onFolderStatesImported() {
        this.itemsList.refresh();
    }

    /**
     * Appellé lors de l'import
     */
    public onFolderStatesHelped() {
        this.itemsList.displayHelpDialog('folderStates');
    }

    public onFolderStatesSorted(folderstates: FolderState[]) {
        this.spinner.show('main-spinner');
        this.foldersStatesService.applyOrderChanges(folderstates).subscribe(
            (x) => {
                this.notificationService.displaySuccess(null, "L'ordre des états de dossier a été appliqué");
                this.itemsList.refresh();
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible dappliquer l'ordre des états de dossier." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Charge les données d'un folderState.
     * @param folderStateId : Id de l'état de dossier à charger.
     */
    private loadFolderState(folderStateId: number): Observable<FolderState> {
        return this.foldersStatesService.get(folderStateId);
    }

    /**
     * Pré-rempli les champs du formulaire.
     * @param folderState : Valeur utilisée pour pré-remplir les champs du formulaire.
     */
    protected fillForm(folderState: IFolderState): FolderStateDetailContext {
        let context = this.createContext();

        context.formContext.item = folderState;
        context.editedItemId = folderState.id;

        return context;
    }

    /**
     * Créer un nouveau contexte.
     */
    protected createContext(): FolderStateDetailContext {
        let context = new FolderStateDetailContext();
        return context;
    }
}
